<template>
    <form
        @change="isFormDirty"
        @submit.prevent="handleSubmit"
        :class="{ 'form-container': typeForm, 'form-modal-container': typeModal }"
        data-cy="edit-container"
    >
        <p class="text-center text-default-red p-2" v-if="validation.message">
            {{ validation.message }}
        </p>
        <div class="form-section">
            <div class="section-body">
                <div data-cy="field-password" class="input-container w-full mb-4">
                    <div class="inline-block w-2/5">
                        <ui-label>
                            {{ $t('user.forms.change_password.password') }}
                            <span class="text-default-red">*</span>
                        </ui-label>
                    </div>
                    <div class="inline-block w-1/2">
                        <ui-input
                            type="password"
                            :class="{ 'form-error': v$.user_password.password.$error }"
                            v-model="v$.user_password.password.$model"
                        />
                    </div>
                    <div class="inline-block w-2/5"></div>
                    <div class="inline-block w-1/2">
                        <div
                            data-cy="field-password-required-error"
                            :class="{ 'text-default-red': v$.user_password.password.$error }"
                            v-if="v$.user_password.password.required.$invalid && v$.user_password.password.$error"
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <div
                            data-cy="field-password-min-length-error"
                            :class="{ 'text-default-red': v$.user_password.password.$error }"
                            v-if="v$.user_password.password.minLength.$invalid && v$.user_password.password.$error"
                        >
                            {{
                                $t('validation.min.string', {
                                    attribute: 'Password',
                                    min: app_config.password_minimum_length,
                                })
                            }}
                        </div>
                        <div
                            data-cy="field-password-numbers-error"
                            :class="{ 'text-default-red': v$.user_password.password.$error }"
                            v-if="v$.user_password.password.includeNumber.$invalid && v$.user_password.password.$error"
                        >
                            {{ $t('validation.include_numbers', { attribute: 'Password' }) }}
                        </div>
                        <div
                            data-cy="field-password-symbols-error"
                            :class="{ 'text-default-red': v$.user_password.password.$error }"
                            v-if="v$.user_password.password.includeSymbol.$invalid && v$.user_password.password.$error"
                        >
                            {{ $t('validation.include_symbols', { attribute: 'Password' }) }}
                        </div>
                        <validation-errors :errors="validation.errors.password" v-if="validation.errors" />
                    </div>
                </div>
                <div data-cy="field-confirm-password" class="input-container w-full">
                    <div class="inline-block w-2/5">
                        <ui-label>
                            {{ $t('user.forms.change_password.confirm_password') }}
                            <span class="text-default-red">*</span>
                        </ui-label>
                    </div>
                    <div class="inline-block w-1/2">
                        <ui-input
                            type="password"
                            :class="{ 'form-error': v$.user_password.password_confirmation.$error }"
                            v-model="v$.user_password.password_confirmation.$model"
                        />
                    </div>
                    <div class="inline-block w-2/5"></div>
                    <div class="inline-block w-1/2">
                        <div
                            data-cy="field-confirm-password-required-error"
                            :class="{ 'text-default-red': v$.user_password.password.$error }"
                            v-if="
                                v$.user_password.password_confirmation.required.$invalid &&
                                v$.user_password.password_confirmation.$error
                            "
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <div
                            data-cy="field-confirm-password-match-error"
                            :class="{ 'text-default-red': v$.user_password.password_confirmation.$error }"
                            v-if="
                                !v$.user_password.password_confirmation.required.$invalid &&
                                v$.user_password.password_confirmation.sameAsPassword.$invalid &&
                                v$.user_password.password_confirmation.$error
                            "
                        >
                            {{ $t('validation.confirmed', { attribute: 'Password' }) }}
                        </div>
                        <validation-errors :errors="validation.errors.password_confirmation" v-if="validation.errors" />
                    </div>
                </div>
                <div class="text-gray-600 text-xs italic">
                    {{ $t('user.forms.change_password.criteria') }}
                    <ul class="list-disc px-4">
                        <li v-if="app_config.password_minimum_length > 0">
                            {{
                                $t('validation.min.string', {
                                    attribute: 'Password',
                                    min: app_config.password_minimum_length,
                                })
                            }}
                        </li>
                        <li v-if="app_config.password_include_numbers">
                            {{ $t('validation.include_numbers', { attribute: 'Password' }) }}
                        </li>
                        <li v-if="app_config.password_include_symbols">
                            {{ $t('validation.include_symbols', { attribute: 'Password' }) }}
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <div class="form-submit" v-if="form_type == 'form'">
            <ui-submit-button :disabled="processing">
                <span v-if="!processing">{{ $t('globals.buttons.save') }}</span>
                <span v-else>{{ $t('globals.buttons.saving') }}</span>
            </ui-submit-button>
        </div>

        <div class="drawer-footer" v-if="form_type == 'drawer'">
            <a-button data-cy="drawer-cancel-btn" class="btn-cancel" @click="onDrawerClose">
                {{ $t('globals.buttons.cancel') }}
            </a-button>
            <a-button data-cy="drawer-save-btn" class="btn-primary" :disabled="processing" @click="handleSubmit">
                <span v-if="!processing">{{ $t('globals.buttons.save') }}</span>
                <span v-else>{{ $t('globals.buttons.saving') }}</span>
            </a-button>
        </div>
    </form>
</template>

<script>
import UiInput from '@/components/ui/Input.vue';
import UiLabel from '@/components/ui/Label.vue';
import UiSubmitButton from '@/components/ui/SubmitButton.vue';
import ValidationErrors from '@/components/ui/ValidationErrors.vue';

import { useVuelidate } from '@vuelidate/core';
import { required, sameAs } from '@vuelidate/validators';

export default {
    setup() {
        return { v$: useVuelidate() };
    },
    components: {
        UiInput,
        UiLabel,
        UiSubmitButton,
        ValidationErrors,
    },
    data() {
        return {
            app_config: {},
        };
    },
    mounted() {
        this.$http
            .all([
                this.$http.post('/api/users/get-config', {
                    id: this.user_password.user_id,
                }),
            ])
            .then(
                this.$http.spread((config) => {
                    let config_data = {
                        ...config.data,
                        password_include_numbers: config.data.password_include_numbers ? true : false,
                        password_include_symbols: config.data.password_include_symbols ? true : false,
                    };

                    this.app_config = config_data;
                }),
            );
    },
    computed: {
        typeForm() {
            return this.form_type !== 'modal';
        },
        typeModal() {
            return this.form_type == 'modal';
        },
    },
    validations: {
        user_password: {
            password: {
                required,
                minLength(value) {
                    if (this.app_config.password_minimum_length > 0) {
                        return value.length >= this.app_config.password_minimum_length;
                    }

                    return true;
                },
                includeNumber(value) {
                    if (this.app_config.password_include_numbers) {
                        return /[0-9]/.test(value);
                    }

                    return true;
                },
                includeSymbol(value) {
                    if (this.app_config.password_include_symbols) {
                        return /[#?!@$%^&*-]/.test(value);
                    }

                    return true;
                },
            },
            password_confirmation: {
                required,
                sameAsPassword(value) {
                    return value === this.user_password.password;
                },
            },
        },
    },
    props: {
        mode: String,
        form_type: {
            type: String,
            default: 'modal',
        },
        user_password: Object,
        validation: Object,
        processing: Boolean,
    },
    methods: {
        isFormDirty() {
            this.$emit('is-form-dirty', this.v$.$anyDirty);
        },
        async handleSubmit() {
            if (!(await this.v$.$validate())) return false;

            this.$emit('formSubmit');
        },
    },
};
</script>
